import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Auth0Provider, Auth0ProviderOptions, useAuth0 } from "@auth0/auth0-react";

import { getClientInfo } from "../redux/reducers/appReducer";
import { isNativeApp, listenForAuthCallbacks, nativeAuthCallbackUrl } from "../native/nativeApp";

/** In the apps, Auth0 returns through the app's URL scheme instead of reloading the page. */
const NativeAuthCallbacks = () => {
  const { handleRedirectCallback } = useAuth0();

  useEffect(
    () =>
      listenForAuthCallbacks(async (url) => {
        const { appState } = await handleRedirectCallback(url);
        window.history.pushState("", "", appState?.returnTo ?? "/");
        // No page load follows in the apps, so tell the router the address changed.
        window.dispatchEvent(new PopStateEvent("popstate"));
      }),
    [handleRedirectCallback],
  );

  return null;
};

const Auth0ProviderCustom: React.FC<React.PropsWithChildren> = ({ children }) => {
  const onRedirectCallback = (appState) => {
    window.history.pushState(
      "",
      "",
      appState && appState.returnTo ? appState.returnTo : window.location.pathname,
    );
  };

  const state = useSelector((state) => getClientInfo(state));

  // Please see https://auth0.github.io/auth0-react/interfaces/auth0provideroptions.html
  // for a full list of the available properties on the provider

  const providerConfig: Auth0ProviderOptions = {
    domain: state.authUrl,
    clientId: state.clientId,
    authorizationParams: {
      audience: state.apiName,
      redirect_uri: isNativeApp ? nativeAuthCallbackUrl(state.authUrl) : window.location.origin,
      scope: state.scope,
    },
    onRedirectCallback,
    // Keep the tokens between visits. With the in-memory default every reload signed in again through a hidden
    // Auth0 iframe, and browsers that block third-party cookies (Safari, Firefox) sent the user through a full
    // redirect to Auth0 and back instead.
    cacheLocation: "localstorage",
    useRefreshTokens: true,
    // auth0-react 1.x fell back to a silent iframe login when no refresh token was cached; keep doing that so
    // sessions started before the upgrade carry on.
    // The apps have no Auth0 cookie for the iframe to use, so they rely on refresh tokens alone.
    useRefreshTokensFallback: !isNativeApp,
  };

  return (
    <Auth0Provider {...providerConfig}>
      {isNativeApp && <NativeAuthCallbacks />}
      {children}
    </Auth0Provider>
  );
};

export default Auth0ProviderCustom;
